import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { Router } from 'vue-router'
import { Injectable } from '@tanbo/vue-di-plugin'
import dayjs from 'dayjs'

import HmacSha256 from 'crypto-js/hmac-sha256'
import Hex from 'crypto-js/enc-hex'
import Base64 from 'crypto-js/enc-base64'
import { CURRENT_PROJECT_SUBJECT } from '@/project-subject/project-subject'
import { getTeamServerBaseUrl } from '@/common/const/api-base-url'
import { defaultErrorAction, errorKeyMap, HttpDefaultErrorHandle } from '@/common/error/error-code'
import { CaptchaCheckService } from '@/services/captcha-check.service'
import { deserialize } from '@/utils/tool'
import { ElMessage } from 'element-plus'

export interface QueryParams {
  [key: string]: number | boolean | string | Array<number | boolean | string>;
}

export interface ResponseCommon {
  ret: 0 | 1
  msg: string
}

export class ResponseError extends Error {
  name = 'ResponseError'

  constructor (public responseBody: any, msg?: string) {
    super(msg)
  }
}

export interface CommonRequestFields {
  uid?: string;
  token?: string;
  team_id?: number;
  /** 与前端 i18n 一致：en-US / zh-CN / ja-JP / zh-TW，随语言切换写入请求 common */
  lang?: string;
}

export const SIGNATURE_INFO = {
  platform: 'pc',
  appId: CURRENT_PROJECT_SUBJECT.sign_app_id,
  key: CURRENT_PROJECT_SUBJECT.sign_key,
}

function urlencodeing (params: string) {
  return params.replace(/\+/ig, '-').replace(/\//ig, '_')
}

@Injectable()
export class Http {
  private axiosInstance: AxiosInstance;

  private commonRequestFields: CommonRequestFields = {}

  /** 需要验证的请求的config集合 */
  private needCaptchaRequestConfigs = [] as any[]
  /** 需要验证的请求的状态处理器 */
  private needCaptchaRequestStatusHandler = new WeakMap()

  private timeDifference: number | undefined

  constructor (private defaultOptions: AxiosRequestConfig = {},
    router: Router,
    defaultErrorHandlers: HttpDefaultErrorHandle[] = [],
    private captchaCheck: CaptchaCheckService) {
    this.axiosInstance = Axios.create(this.defaultOptions)
    this.axiosInstance.interceptors.request.use(request => {
      request.data = deserialize(request.data)
      request.headers.common['X-Cf-Platform'] = SIGNATURE_INFO.platform
      request.headers.common['X-Cf-Appid'] = SIGNATURE_INFO.appId
      request.headers.common['Accept-Language'] = this.getAcceptLanguage()
      request.data.common = {
        ...this.commonRequestFields,
        ...request.data.common
      }
      return request
    })
    this.axiosInstance.interceptors.response.use((response: any) => {
      const body = response.data
      body.resp_common = body.resp_common || {}
      const { ret, msg } = body.resp_common

      // 需要人机校验
      if (body.resp_common.ret === errorKeyMap.HUMAN_NEED_VERIFY.ret) {
        return this.captchaHandle(response)
      }

      if (ret === 0) {
        return body
      }

      const timer = setTimeout(() => {
        for (const item of defaultErrorHandlers) {
          if (Array.isArray(item.errorCode) ? item.errorCode.includes(ret) : item.errorCode === ret) {
            item.handler(ret, msg)
          }
        }
        // eslint-disable-next-line no-magic-numbers
      }, 10)

      Object.defineProperty(body.resp_common, 'ret', {
        get () {
          clearTimeout(timer)
          return ret
        }
      })
      return Promise.reject(new ResponseError(body.resp_common, body.resp_common?.msg || '未知错误！'))
    })
    this.timeSync()
  }

  captchaHandle (response: any) {
    this.initCaptcha()
    return this.collectNeedCaptchaRequest(response)
  }

  initCaptcha () {
    this.captchaCheck.check().then((res) => {
      this.callNeedCaptchaRequest()
    }).catch(() => {
      this.rejectNeedCaptchaRequest()
    })
  }

  collectNeedCaptchaRequest (response: any) {
    return new Promise((resolve, reject) => {
      this.needCaptchaRequestConfigs.push(response.config)
      this.needCaptchaRequestStatusHandler.set(response.config, { resolve, reject })
    })
  }

  callNeedCaptchaRequest () {
    const removeNeedCaptchaRequest = (config: any) => {
      const idx = this.needCaptchaRequestConfigs.indexOf(config) // 防止跳项
      if (idx === -1) return
      this.needCaptchaRequestConfigs.splice(idx, 1)
      this.needCaptchaRequestStatusHandler.delete(config)
    }

    let isShowError = false // 避免toast重复
    for (let i = 0; i < this.needCaptchaRequestConfigs.length; i++) {
      let config = this.needCaptchaRequestConfigs[i]
      config.isCallNeedCaptchaRequest = true
      const handler = this.needCaptchaRequestStatusHandler.get(config)

      if (!handler || typeof handler.resolve !== 'function' || typeof handler.reject !== 'function') {
        handler.reject(new Error('找不到对应的状态处理器'))
        return
      }

      this.getConfigWithCaptcha(config).then(config => {
        this.axiosInstance.request(config).then(response => {
          removeNeedCaptchaRequest(config)
          handler.resolve(response)
        }).catch(error => {
          // 重新发起人机校验
          if ([errorKeyMap.HUMAN_VERIFY_ERROR.ret].includes(error.responseBody?.ret)) {
            this.initCaptcha()
            !isShowError && setTimeout(() => ElMessage.error('图形验证码错误，请重新校验'), 300)
            isShowError = true
          } else {
            removeNeedCaptchaRequest(config)
            defaultErrorAction(error)
            handler.reject(error)
          }
        })
      })
    }
  }

  setBaseUrl (baseURL: string) {
    this.defaultOptions.baseURL = baseURL
    this.axiosInstance.defaults.baseURL = baseURL
    this.timeDifference = undefined
    void this.timeSync().catch(() => {
      this.timeDifference = 0
    })
  }

  rejectNeedCaptchaRequest () {
    this.needCaptchaRequestConfigs.forEach(config => {
      const handler = this.needCaptchaRequestStatusHandler.get(config)

      if (!handler || typeof handler.resolve !== 'function' || typeof handler.reject !== 'function') {
        handler.reject(new Error('找不到对应的状态处理器'))
        return
      }

      this.needCaptchaRequestStatusHandler.delete(config)
      handler.reject(new Error('未验证通过'))
    })
    this.needCaptchaRequestConfigs = [] as any[]
  }

  async getConfigWithCaptcha (config: any) {
    config.data = deserialize(config.data)
    if (!(this.captchaCheck.captchaId && config.data)) {
      return config
    }
    if (this.captchaCheck.captchaId && config.data) {
      config.data.captcha_id = this.captchaCheck.captchaId
      config.data.captcha_code = this.captchaCheck.captchaCode
      config = await this.updateRequestSign(config)
    }
    return config
  }

  timeSync () {
    return Axios.get(`${getTeamServerBaseUrl()}/st?t=${Date.now()}`).then((res: any) => {
      // eslint-disable-next-line no-magic-numbers
      this.timeDifference = res.data?.st_s - parseInt(String(Date.now() / 1000))
    })
  }

  getCommonRequestFields () {
    return this.commonRequestFields
  }

  addCommonRequestFields (params: CommonRequestFields) {
    Object.assign(this.commonRequestFields, params)
  }

  cleanCommonRequestFields () {
    const { lang } = this.commonRequestFields
    this.commonRequestFields = lang !== undefined ? { lang } : {}
  }

  /** 与 i18n locale 一致，供请求头 Accept-Language 与 data.common.lang 使用 */
  private getAcceptLanguage (): string {
    return this.commonRequestFields.lang || 'zh-CN'
  }

  async updateRequestSign (request: any) {
    const { url, data } = request
    const { timestamp, nonce, signature } = await this.getSign(url, data)
    request.headers['X-Cf-Ts'] = timestamp
    request.headers['X-Cf-Nonce'] = nonce
    request.headers['X-Cf-Signature'] = signature
    request.headers['Accept-Language'] = this.getAcceptLanguage()
    return request
  }


  private async getSign (url: string, data: any) {
    if (this.timeDifference === undefined) {
      await this.timeSync().catch(() => {
        this.timeDifference = 0
      })
    }

    url = url.replace(/^.*?\/api\//, '/').replace(/^([^/])/, '/$1')

    const commonRequestFields = data.common?.token ? data.common : this.commonRequestFields
    const teamId = commonRequestFields.team_id || -1

    const deviceId = commonRequestFields.uid || 'xxx'
    // eslint-disable-next-line no-magic-numbers
    const timestamp = (Date.now() / 1000 + (this.timeDifference ?? 0)).toFixed(0)
    // eslint-disable-next-line no-magic-numbers
    const nonce = HmacSha256(`${timestamp}+${deviceId}+${(Math.random() * 100000).toFixed(0)}`, SIGNATURE_INFO.key).toString(Hex)
    const hmacStr = `${JSON.stringify({
      ...data,
      common: commonRequestFields,
    })}${SIGNATURE_INFO.platform}${deviceId}${SIGNATURE_INFO.appId}${timestamp}${nonce}POST/api${url}`
    const signature = HmacSha256(hmacStr, SIGNATURE_INFO.key).toString(Base64)

    return {
      timestamp,
      nonce,
      signature: urlencodeing(signature),
      deviceId,
      teamId
    }
  }

 async get<T = any> (url: string,
    params: QueryParams = {},options: any = {}): Promise<T & { resp_common: ResponseCommon }> {
    const { timestamp, nonce, signature, deviceId, teamId } = await this.getSign(url, params)
    return this.axiosInstance.get(url, {
      params,
      ...options,
      headers: {
       'X-Cf-Ts': timestamp,
        'X-Cf-Nonce': nonce,
        'X-Cf-Signature': signature,
        'X-Cf-Device-Id': deviceId,
        'X-Cf-TeamID': teamId,
        ...(options.headers && typeof options.headers === 'object' ? options.headers : {}),
        'Accept-Language': this.getAcceptLanguage(),
      }
    })
  }

  async post<T = any> (url: string, data: any, options: any = {}): Promise<T & { resp_common: ResponseCommon }> {
    const postData = (data || {})
    const { timestamp, nonce, signature, deviceId, teamId } = await this.getSign(url, postData)
    return this.axiosInstance.post(url, {
      ...postData
    }, {
      ...options,
      headers: {
        'X-Cf-Ts': timestamp,
        'X-Cf-Nonce': nonce,
        'X-Cf-Signature': signature,
        'X-Cf-Device-Id': deviceId,
        'X-Cf-TeamID': teamId,
        ...(options.headers && typeof options.headers === 'object' ? options.headers : {}),
        'Accept-Language': this.getAcceptLanguage(),
      }
    })
  }

  async downloadWithBlob (url: string, data: any, moduleName: string) {
    const postData = {
      common: {
        ...this.commonRequestFields
      },
      ...data
    }
    const { timestamp, nonce, signature, deviceId, teamId } = await this.getSign(url, postData)

    Axios({
      url: this.defaultOptions.baseURL + url,
      method: 'POST',
      headers: {
        'X-Cf-Platform': SIGNATURE_INFO.platform,
        'X-Cf-Appid': SIGNATURE_INFO.appId,
        'X-Cf-Ts': timestamp,
        'X-Cf-Nonce': nonce,
        'X-Cf-Signature': signature,
        'X-Cf-Device-Id': deviceId,
        'X-Cf-TeamID': teamId,
        'Accept-Language': this.getAcceptLanguage(),
      },
      responseType: 'blob',
      data: postData
    }).then((response) => {
      const fileUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = fileUrl
      /** 与导出约定一致：file_type 为 0 时导出 csv；其余（含未传，如策略模板）默认 .xlsx */
      const ft = postData.file_type
      const downloadExt = ft === 0 || ft === '0' ? 'csv' : 'xlsx'
      const fileName = `${moduleName}${dayjs().format('YYYYMMDDHHmmss')}.${downloadExt}`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })
  }

  /**
   * GET 下载二进制（如策略模板）。
   *
   * 浏览器禁止 GET 带 body，因此不会出现 Apifox 里那种 `--data-raw '{"common":{...}}'`；
   * 从 Chrome 复制的 curl 里，`common` 在 **query** 上：`...&common=%7B%22uid%22:...%7D`（JSON 再 URL 编码），
   * 与验签用的对象 `{ ...业务参数, common }` 一致（axios `params` 对 object 会 `JSON.stringify` 后拼进 URL）。
   */
  async downloadGetWithBlob (url: string, params: QueryParams = {}, moduleName: string, ext = 'xlsx') {
    const raw = params as Record<string, unknown>
    const commonFromCaller = raw.common
    const commonMerged: CommonRequestFields = {
      ...this.commonRequestFields,
      ...(typeof commonFromCaller === 'object' && commonFromCaller !== null && !Array.isArray(commonFromCaller)
        ? (commonFromCaller as CommonRequestFields)
        : {}),
    }
    const { common: _omitCommon, ...queryOnly } = raw
    const signPayload = {
      ...queryOnly,
      common: commonMerged,
    }
    const { timestamp, nonce, signature, deviceId, teamId } = await this.getSign(url, signPayload)
    let fullUrl = url
    if (!url.includes('://')) {
      fullUrl = (this.defaultOptions.baseURL || '') + url
    }
    const response = await Axios({
      url: fullUrl,
      method: 'GET',
      params: signPayload,
      responseType: 'blob',
      headers: {
        'X-Cf-Platform': SIGNATURE_INFO.platform,
        'X-Cf-Appid': SIGNATURE_INFO.appId,
        'X-Cf-Ts': timestamp,
        'X-Cf-Nonce': nonce,
        'X-Cf-Signature': signature,
        'X-Cf-Device-Id': deviceId,
        'X-Cf-TeamID': teamId,
        'Accept-Language': this.getAcceptLanguage(),
      },
    })
    const blob = response.data as Blob
    if (blob?.type?.toLowerCase().includes('application/json')) {
      const text = await new Response(blob).text()
      let json: { resp_common?: { msg?: string }; msg?: string } | null = null
      try {
        json = JSON.parse(text)
      } catch {
        throw new Error('下载失败')
      }
      const msg = json?.resp_common?.msg || json?.msg || '下载失败'
      throw new Error(typeof msg === 'string' ? msg : '下载失败')
    }
    if (response.status < 200 || response.status >= 300) {
      throw new Error(`下载失败 (${response.status})`)
    }
    const fileUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = fileUrl
    const fileName = `${moduleName}${dayjs().format('YYYYMMDDHHmmss')}.${ext}`
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  async postForm<T = any> (url: string, data: any, uploadProgressFunction: (progress: any) => void = () => {}, config: any = {}): Promise<T & { resp_common: ResponseCommon }> {
    // 如果 data 是 FormData，需要特殊处理
    let postData: any
    if (data instanceof FormData) {
      // FormData 不能直接合并，需要提取参数用于签名计算
      const params: Record<string, any> = {}
      for (const [key, value] of data.entries()) {
        if (key !== 'file') {
          params[key] = value
        }
      }
      postData = {
        common: {
          ...this.commonRequestFields
        },
        ...params
      }
    } else {
      postData = {
        common: {
          ...this.commonRequestFields
        },
        ...data
      }
    }

    const { timestamp, nonce, signature, deviceId,teamId } = await this.getSign(url, postData)

    if (!url.includes('://')) {
      url = this.defaultOptions.baseURL + url
    }

    return Axios({
      ...config,
      url,
      method: 'POST',
      headers: {
        'X-Cf-Platform': SIGNATURE_INFO.platform,
        'X-Cf-Appid': SIGNATURE_INFO.appId,
        'X-Cf-Ts': timestamp,
        'X-Cf-Nonce': nonce,
        'X-Cf-Signature': signature,
        'X-Cf-Device-Id': deviceId,
        'X-Cf-TeamID': teamId,
        ...config.headers,
        'Accept-Language': this.getAcceptLanguage(),
      },
      data, // FormData 直接传递，不要包装
      onUploadProgress: uploadProgressFunction
    }).then(response => {
      return response.data
    })
  }
}
